import { useEffect, useState } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const stringifyDetails = (details) => {
  if (!details) return '';
  if (typeof details === 'string') return details;

  try {
    return JSON.stringify(details);
  } catch (error) {
    return '';
  }
};

const normalizeEventRow = (event) => ({
  id: event.id,
  occurredAt: event.createdAt,
  action: event.eventType || 'Profile update',
  summary: event.summary || '',
  actor: event.actorName || event.actorEmail || 'Unknown actor',
  details: ''
});

const normalizeAuditLogRow = (auditLog) => ({
  id: auditLog.id,
  occurredAt: auditLog.timestamp,
  action: auditLog.actionType || 'Profile update',
  summary: auditLog.targetName || '',
  actor: auditLog.userName || auditLog.userEmail || 'Unknown actor',
  details: stringifyDetails(auditLog.details)
});

const normalizeLegacyRow = (row) => ({
  id: row.id,
  occurredAt: row.createdAt || row.timestamp,
  action: row.action || row.event || 'Profile update',
  summary: row.summary || row.targetName || '',
  actor: row.actor || row.createdBy || 'Unknown actor',
  details: stringifyDetails(row.details)
});

const normalizeAuditRows = (data) => [
  ...(data.events || []).map(normalizeEventRow),
  ...(data.auditLogs || []).map(normalizeAuditLogRow),
  ...(data.audit || data.rows || []).map(normalizeLegacyRow)
];

export default function LocationAuditPanel({ locationId }) {
  const authenticatedFetch = useAuthenticatedFetch();
  const [state, setState] = useState({ loading: true, error: '', rows: [] });

  useEffect(() => {
    let isMounted = true;

    const loadAudit = async () => {
      if (!locationId) {
        setState({ loading: false, error: '', rows: [] });
        return;
      }

      setState({ loading: true, error: '', rows: [] });
      try {
        const response = await authenticatedFetch(`/api/locations/profiles/${locationId}/audit`);
        const data = await readJson(response);
        if (!response.ok) throw new Error(data.error || 'Failed to load location audit');
        if (isMounted) setState({ loading: false, error: '', rows: normalizeAuditRows(data) });
      } catch (error) {
        if (isMounted) setState({ loading: false, error: error.message, rows: [] });
      }
    };

    loadAudit();

    return () => {
      isMounted = false;
    };
  }, [authenticatedFetch, locationId]);

  return (
    <section id="audit" className="rounded-lg border border-white/10 bg-[#0b0b0b] p-4">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-white">Location Audit</h2>
        <p className="text-xs text-gray-500">Administrative profile changes</p>
      </div>

      {state.error && (
        <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
          {state.error}
        </div>
      )}

      {state.loading ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-gray-500">Loading audit...</div>
      ) : state.rows.length > 0 ? (
        <div className="divide-y divide-white/10 overflow-hidden rounded-lg border border-white/10">
          {state.rows.map(row => (
            <div key={row.id || `${row.occurredAt}-${row.action}`} className="grid gap-2 bg-white/[0.02] p-3 text-sm md:grid-cols-[10rem_1fr_12rem]">
              <span className="text-gray-500">{row.occurredAt || 'Unknown time'}</span>
              <span className="min-w-0">
                <span className="block font-semibold text-white">{row.action || 'Profile update'}</span>
                {row.summary && <span className="block truncate text-xs text-gray-500">{row.summary}</span>}
                {row.details && <span className="block truncate text-xs text-gray-500">{row.details}</span>}
              </span>
              <span className="text-gray-400">{row.actor || 'Unknown actor'}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-gray-500">No audit entries</div>
      )}
    </section>
  );
}
